import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { services, type Service } from '../data/site';
import { Reveal } from './Reveal';
import { ButtonLink } from './ui/Button';
function ServiceItems({
  items
}: {
  items: string[];
}) {
  return <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2" data-id="element-233" data-mp="1dw5a5ic3e7kr">
      {items.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-234" data-mp="18x2886reshh0">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-cobalt-700" strokeWidth={3} aria-hidden="true" data-id="element-235" data-mp="13wmu3czaz5xz" />
          <span className="text-[15px] leading-snug text-charcoal-600" data-id="element-236" data-mp="u61ov6p4j363">
            {item}
          </span>
        </li>)}
    </ul>;
}
function FeaturedService({
  service
}: {
  service: Service;
}) {
  return <Reveal as="article" className="grid overflow-hidden rounded-lg border border-charcoal-200 bg-white lg:grid-cols-2" data-id="element-237" data-mp="l5wo3fefr30w">
      <div className="relative min-h-[280px] lg:min-h-[420px]" data-id="element-238" data-mp="g6sguub2nfd7">
        <img src={service.image} alt={service.alt} className="absolute inset-0 h-full w-full object-cover" loading="lazy" data-id="element-239" data-mp="36c7rid7z0yqa" />
      </div>
      <div className="flex flex-col p-7 sm:p-10" data-id="element-240" data-mp="2xc1jh6v1kqyi">
        <h3 className="text-2xl font-extrabold leading-tight tracking-tight text-charcoal-900 sm:text-[1.75rem]" data-id="element-241" data-mp="2xdcmv5kkrwp3">
          {service.title}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-charcoal-600" data-id="element-242" data-mp="3pzxco1lek0qj">
          {service.description}
        </p>
        <div className="mt-7 border-t border-charcoal-100 pt-6" data-id="element-243" data-mp="3u2bsectrykdl">
          <ServiceItems items={service.items} data-id="element-244" data-mp="3g29fz648kj5q" />
        </div>
      </div>
    </Reveal>;
}
function ServiceCard({
  service,
  delay
}: {
  service: Service;
  delay: number;
}) {
  return <Reveal as="article" delay={delay} className="group flex h-full flex-col overflow-hidden rounded-lg border border-charcoal-200 bg-white" data-id="element-245" data-mp="3l36yjjt5rf7r">
      <div className="h-52 overflow-hidden" data-id="element-246" data-mp="29ns00blawfaj">
        <img src={service.image} alt={service.alt} className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" loading="lazy" data-id="element-247" data-mp="1pjuj5e3il8h3" />
      </div>
      <div className="flex flex-1 flex-col p-7" data-id="element-248" data-mp="1gqqz2myd5xdh">
        <h3 className="text-xl font-extrabold leading-snug tracking-tight text-charcoal-900" data-id="element-249" data-mp="1gpfuikx3gllb">
          {service.title}
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-charcoal-600" data-id="element-250" data-mp="16tuyd3epifjh">
          {service.description}
        </p>
        <div className="mt-6 border-t border-charcoal-100 pt-5" data-id="element-251" data-mp="11tqf55mez6pk">
          <ServiceItems items={service.items} data-id="element-252" data-mp="xsn36u7hlutm" />
        </div>
      </div>
    </Reveal>;
}
export function Services() {
  const [featured, ...rest] = services;
  return <section id="services" aria-labelledby="services-heading" className="border-y border-charcoal-100 bg-charcoal-50 py-20 lg:py-28" data-id="element-253" data-mp="sw6cptctf4dd">
      <div className="mx-auto max-w-container px-5 lg:px-8" data-id="element-254" data-mp="2n8w4a4fpo1hl">
        <Reveal className="max-w-2xl" data-id="element-255" data-mp="2urnasi71unpr">
          <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-cobalt-700" data-id="element-256" data-mp="2uqkkochjwpaw">
            What We Do
          </p>
          <h2 id="services-heading" className="mt-4 text-3xl font-extrabold leading-[1.12] tracking-tight text-charcoal-900 sm:text-[2.75rem]" data-id="element-257" data-mp="2zodqv5iutjb6">
            Commercial &amp; Industrial Services
          </h2>
          <p className="mt-5 text-lg text-charcoal-600" data-id="element-258" data-mp="2bt17qxrz2mj7">
            Professional solutions designed around your property.
          </p>
        </Reveal>

        <div className="mt-12 space-y-6" data-id="element-259" data-mp="2gtozjlo0z7eh">
          <FeaturedService service={featured} data-id="element-260" data-mp="2lq5xt8oxv25a" />
          <div className="grid gap-6 md:grid-cols-2" data-id="element-261" data-mp="2lpe3003618g2">
            {rest.map((service, index) => <ServiceCard key={service.id} service={service} delay={index * 0.06} data-id="element-262" data-mp="2ptlug510d2hz" data-mpi={index} />)}
          </div>
        </div>

        <Reveal className="mt-12 flex flex-col items-start gap-5 rounded-lg border border-charcoal-200 bg-white p-7 sm:flex-row sm:items-center sm:justify-between sm:p-8" data-id="element-263" data-mp="50h1areggbox">
          <p className="text-lg font-bold tracking-tight text-charcoal-900" data-id="element-264" data-mp="3us8z5kdmwhlm">
            Need something specific? Talk to us about your project.
          </p>
          <ButtonLink href="#contact" size="lg" className="shrink-0" data-id="element-265" data-mp="8jz8zlvwc9uo">
            Discuss Your Project
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-266" data-mp="mjqu06ktyqi6" />
          </ButtonLink>
        </Reveal>
      </div>
    </section>;
}